import { useMemo, useState } from 'react';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import { DEVICE_LABEL_KEYS } from './constants';
import { useTranslation } from 'react-i18next';

const EMPTY_VALUE = '—';

const COLUMNS = [
  { key: 'label', labelKey: 'linkClicks.linkNameUrl' },
  { key: 'pageTitle', labelKey: 'linkClicks.page' },
  { key: 'totalClicks', labelKey: 'linkClicks.totalClicks', sortable: true },
  { key: 'uniqueClicks', labelKey: 'linkClicks.uniqueClicks', sortable: true },
  { key: 'ctr', labelKey: 'linkClicks.ctr', sortable: true },
  { key: 'topCountry', labelKey: 'linkClicks.topCountry' },
  { key: 'topDevice', labelKey: 'linkClicks.topDevice' },
  { key: 'lastClickAt', labelKey: 'linkClicks.lastClick', sortable: true }
];

const formatDate = (date, locale) =>
  new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).format(new Date(date));

const LinkPerformanceTable = ({ links }) => {
  const { t, i18n } = useTranslation();
  const [sort, setSort] = useState({ key: 'totalClicks', direction: 'desc' });

  const sortedLinks = useMemo(() => {
    return [...links].sort((firstLink, secondLink) => {
      const firstValue = firstLink[sort.key];
      const secondValue = secondLink[sort.key];

      if (firstValue === secondValue) return 0;
      if (firstValue === null) return 1;
      if (secondValue === null) return -1;

      const order = firstValue > secondValue ? 1 : -1;
      return sort.direction === 'asc' ? order : -order;
    });
  }, [links, sort]);

  const handleSort = (key) => {
    setSort((currentSort) => {
      const isCurrentColumn = currentSort.key === key;

      return {
        key,
        direction: isCurrentColumn
          ? currentSort.direction === 'asc'
            ? 'desc'
            : 'asc'
          : 'asc'
      };
    });
  };

  const renderSortIcon = (key) => {
    if (sort.key !== key) {
      return <BsCaretDownFill className="text-gray" aria-hidden="true" />;
    }

    const SortIcon = sort.direction === 'asc' ? BsCaretUpFill : BsCaretDownFill;
    return <SortIcon className="text-primary2" aria-hidden="true" />;
  };

  return (
    <section className="bg-white rounded-4 p-3 p-md-4">
      <h2 className="fs-4 fw-bold mb-1">{t('linkClicks.linkPerformance')}</h2>
      <p className="fs-7 text-gray mb-4">{t('linkClicks.linkPerformanceDescription')}</p>

      <div className="table-responsive">
        <table className="table link-clicks-table align-middle text-slate-gray mb-0">
          <thead className="border-0">
            <tr>
              {COLUMNS.map(({ key, labelKey, sortable }) => (
                <th
                  key={key}
                  scope="col"
                  aria-sort={
                    sort.key === key
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  className="border-0 px-2 py-2 fs-7 text-nowrap"
                >
                  {sortable ? (
                    <button
                      type="button"
                      className="d-inline-flex align-items-center gap-1 bg-transparent fw-bold text-dark"
                      onClick={() => handleSort(key)}
                    >
                      {t(labelKey)}
                      {renderSortIcon(key)}
                    </button>
                  ) : (
                    t(labelKey)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedLinks.map((link, index) => (
              <tr key={link.linkId} className={`${sortedLinks.length - 1 !== index ? "border-soft-gray" : "border-white"}`}>
                <td className="px-2 py-3">
                  <div className="fw-bold fs-7">{link.label || "—"}</div>
                  <div className="text-gray fs-9">{link.url}</div>
                </td>
                <td className="px-2 py-3 fs-7 text-nowrap">
                  {link.pageTitle ?? EMPTY_VALUE}
                </td>
                <td className="px-2 py-3 fs-7">{link.totalClicks}</td>
                <td className="px-2 py-3 fs-7">{link.uniqueClicks}</td>
                <td className="px-2 py-3 fs-7">
                  {link.ctr === null ? EMPTY_VALUE : `${link.ctr.toFixed(1)}%`}
                </td>
                <td className="px-2 py-3 fs-7">
                  {link.topCountry ?? EMPTY_VALUE}
                </td>
                <td className="px-2 py-3 fs-7">
                  {t(DEVICE_LABEL_KEYS[link.topDevice])}
                </td>
                <td className="px-2 py-3 fs-7 text-nowrap">
                  {link.lastClickAt
                    ? formatDate(link.lastClickAt, i18n.language)
                    : EMPTY_VALUE}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!sortedLinks.length && (
        <div className="text-center text-gray py-5">
          {t('linkClicks.noMatchingLinks')}
        </div>
      )}
    </section>
  );
};

export default LinkPerformanceTable;
